/**
 * App-wide previous-route tracker.
 *
 * Records each in-app navigation so the feedback context can report the
 * page the user came from. React Router does not expose a referrer, and
 * `document.referrer` never updates on client-side navigation, so we
 * keep a tiny module-scoped history here.
 *
 * Mirrors the `lastRequestId` / `getLastRequestId()` pattern in
 * `services/api.ts` - a module-level value fed by an app-level effect
 * (`useRecordRouteHistory` in `App.tsx`) and read on demand.
 */

let currentRoute: string | null = null
let previousRoute: string | null = null

/**
 * Record the route the user is now on (pathname + query). The prior
 * distinct route becomes the "previous" route. Repeat calls with the
 * same value are ignored so a re-render never clobbers the referrer.
 */
export function recordRoute(route: string): void {
  if (route === currentRoute) return
  previousRoute = currentRoute
  currentRoute = route
}

/** The route the user was on before the current one (null on first load). */
export function getPreviousRoute(): string | null {
  return previousRoute
}

/** Test-only: reset the recorded history between cases. */
export function resetRouteHistory(): void {
  currentRoute = null
  previousRoute = null
}
